import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lightning, LockSimple, ShieldCheck, LockKey, QrCode, CreditCard } from "@phosphor-icons/react";
import ProtectedVideo from "@/components/ProtectedVideo";
import { brl } from "@/lib/api";
import { WatchGate } from "./WatchGate";
import { PlanPicker } from "./PlanPicker";
import "./videobuy.css";

const trust = [
  { icon: ShieldCheck, label: "Compra 100% segura" },
  { icon: Lightning, label: "Acesso imediato" },
  { icon: LockKey, label: "Dados protegidos" },
];

const fade = (d = 0) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] } });

export const VideoBuyStage = ({ data, plans, selectedPlan = 0, onSelectPlan, onBuy, compact = false }) => {
  const [watchPct, setWatchPct] = useState(0);
  const required = Number(data.require_watch_percent) || 50;
  const unlocked = !data.require_watch || watchPct >= required;
  const vertical = !!data.video_vertical;
  const hasPlans = plans && plans.length > 1;

  return (
    <section className="relative" data-testid="video-buy-stage">
      <motion.div {...fade(0)} className="text-center">
        <span className={`inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-semibold text-slate-300 ${compact ? "text-[10px]" : "text-[11px] sm:text-xs"}`}>
          <span className="vb-dot" /> Oferta disponível agora
        </span>
        <h1 className={`font-display font-black tracking-tight leading-[1.05] mt-4 mx-auto max-w-4xl break-words ${compact ? "text-[26px]" : "text-[clamp(1.85rem,5.2vw,3.4rem)]"}`} data-testid="public-video-title">
          {data.title || "Título da sua oferta"}
        </h1>
        {data.headline && (
          <p className={`text-slate-300 mt-3 mx-auto max-w-2xl leading-relaxed ${compact ? "text-[13px]" : "text-[15px] sm:text-lg"}`} data-testid="public-video-headline">
            {data.headline}
          </p>
        )}
      </motion.div>

      <motion.div {...fade(0.1)} className={`mx-auto ${compact ? "mt-5" : "mt-7 sm:mt-10"} ${vertical ? (compact ? "max-w-[240px]" : "max-w-[360px]") : "max-w-4xl"}`}>
        <div className="vb-video-frame">
          <div className="vb-video-inner" data-testid="public-video-player">
            {data.video_url ? (
              <ProtectedVideo url={data.video_url} onProgress={setWatchPct} aspectRatio={vertical ? "9/16" : undefined} />
            ) : (
              <div className={`w-full flex items-center justify-center text-slate-500 text-sm ${vertical ? "aspect-[9/16]" : "aspect-video"}`}>Cole a URL do vídeo</div>
            )}
          </div>
        </div>
      </motion.div>

      <motion.div {...fade(0.2)} className={`mx-auto ${compact ? "mt-4" : "mt-6 sm:mt-8"} max-w-xl`}>
        {data.require_watch && <WatchGate pct={watchPct} required={required} compact={compact} />}
        <div className={`vb-card mt-4 ${compact ? "p-4" : "p-5 sm:p-6"}`} data-testid="public-buy-panel">
          {hasPlans && <PlanPicker plans={plans} selected={selectedPlan} onSelect={onSelectPlan} />}
          <div className={`flex items-end justify-between gap-3 ${hasPlans ? "mt-4" : ""}`}>
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Investimento</div>
              <div className={`font-display font-black text-[#FFB30F] tabular-nums leading-none mt-1 ${compact ? "text-3xl" : "text-4xl sm:text-5xl"}`} data-testid="public-price">
                {brl(data.price)}
              </div>
            </div>
            <div className="flex gap-1.5 pb-1">
              {data.accept_pix !== false && <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 px-2 py-1 text-[11px] font-semibold"><QrCode size={13} /> Pix</span>}
              {data.accept_card && <span className="inline-flex items-center gap-1 rounded-lg bg-indigo-500/10 border border-indigo-400/25 text-indigo-300 px-2 py-1 text-[11px] font-semibold"><CreditCard size={13} /> Cartão</span>}
            </div>
          </div>
          <button type="button" onClick={onBuy} disabled={!unlocked} className={`vb-cta vb-cta-pulse mt-5 ${compact ? "!text-base !min-h-[52px]" : ""}`} data-testid="public-buy-cta-button">
            {unlocked ? (<><Lightning size={20} weight="fill" /> {data.button_text || "Comprar agora"}</>) : (<><LockSimple size={18} weight="bold" /> Assista {required}% para liberar</>)}
          </button>
          <div className={`grid grid-cols-3 gap-2 mt-5 ${compact ? "text-[9px]" : "text-[10px] sm:text-xs"}`}>
            {trust.map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 text-center text-slate-400">
                <Icon size={15} className="text-[#FFB30F] shrink-0" weight="duotone" /> <span className="leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};
